import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Heart, Mail, Phone, MapPin } from 'lucide-react';
import { scrollHomeSectionIntoView, HOME_SCROLL_TARGET_KEY } from '@/lib/scrollHomeSection';
import { wsjSocialLinks } from '@/lib/wsjSocialLinks';

const Footer = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const scrollToSection = (id) => {
    scrollHomeSectionIntoView(id);
  };

  const goToSection = (id) => {
    if (pathname === '/') {
      scrollToSection(id);
      return;
    }
    sessionStorage.setItem(HOME_SCROLL_TARGET_KEY, id);
    navigate('/');
  };

  const footerSections = [
    {
      title: t.footer.servicesTitle,
      links: [
        { text: t.footer.servicesList.web, action: () => goToSection('services') },
        { text: t.footer.servicesList.ecommerce, action: () => goToSection('services') },
        { text: t.footer.servicesList.marketing, action: () => goToSection('services') },
        { text: t.footer.servicesList.automation, action: () => goToSection('services') },
        { text: t.footer.servicesList.mobile, action: () => goToSection('services') },
        { text: t.servicesNav.wsjPedidos, action: () => navigate('/wsj-pedidos') }
      ]
    },
    {
      title: t.footer.companyTitle,
      links: [
        { text: t.footer.companyList.about, action: () => goToSection('about') },
        { text: t.footer.companyList.methodology, action: () => goToSection('pyramid') },
        { text: t.footer.companyList.founder, action: () => goToSection('founder') },
        { text: t.footer.companyList.contact, action: () => goToSection('contact') }
      ]
    }
  ];

  return (
    <footer className="border-t border-white/10 relative w-full min-w-0 overflow-x-hidden">
      <div className="absolute inset-0 tech-grid opacity-10"></div>
      <div className="container mx-auto w-full max-w-full box-border px-4 sm:px-6 lg:px-8 py-16 relative z-10">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          <div className="lg:col-span-2">
            <div className="mb-6">
              <div className="text-3xl font-black tracking-tight mb-3 flex items-center">
                <span className="text-white">WSJ</span>
                <span className="text-red-600 ml-1 text-4xl">.</span>
              </div>
              <p className="text-white/40 leading-relaxed max-w-md font-light">
                {t.footer.tagline}
              </p>
            </div>

            <div className="space-y-3 min-w-0">
              <a 
                href="mailto:williamsartijose@hotmail.com"
                className="flex items-start gap-3 text-white/40 hover:text-yellow-500 transition-colors min-w-0"
              >
                <Mail className="w-5 h-5 shrink-0 mt-0.5" />
                <span className="text-sm break-words min-w-0">williamsartijose@hotmail.com</span>
              </a>
              <a 
                href="tel:+351924963314"
                className="flex items-center gap-3 text-white/40 hover:text-yellow-500 transition-colors min-w-0"
              >
                <Phone className="w-5 h-5 shrink-0" />
                <span className="text-sm break-words min-w-0">{t.contact.phonePortugalDisplay}</span>
              </a>
              <a 
                href="tel:+5518997874643"
                className="flex items-center gap-3 text-white/40 hover:text-yellow-500 transition-colors min-w-0"
              >
                <Phone className="w-5 h-5 shrink-0" />
                <span className="text-sm break-words min-w-0">{t.contact.phoneBrazilDisplay}</span>
              </a>
              <div className="flex items-start gap-3 text-white/40 min-w-0">
                <MapPin className="w-5 h-5 shrink-0 mt-0.5" />
                <span className="text-sm break-words min-w-0">{t.contact.locationPortugal}</span>
              </div>
              <div className="flex items-start gap-3 text-white/40 min-w-0">
                <MapPin className="w-5 h-5 shrink-0 mt-0.5" />
                <span className="text-sm break-words min-w-0">{t.contact.locationBrazil}</span>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-white/[0.06]">
              <h4 className="text-sm font-semibold text-white/70 mb-4 tracking-wide">{t.footer.followTitle}</h4>
              <div className="flex flex-wrap items-center gap-2.5">
                {wsjSocialLinks.map(({ url, label, Icon }) => (
                  <a
                    key={url}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    title={label}
                    className="group relative flex h-10 w-10 items-center justify-center overflow-hidden rounded-xl border border-white/10 bg-white/5 text-white/50 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)] backdrop-blur-md transition-all duration-300 hover:scale-105 hover:border-amber-400/55 hover:bg-amber-500/10 hover:text-amber-300 hover:shadow-[0_0_28px_-6px_rgba(251,191,36,0.5),inset_0_1px_0_0_rgba(255,255,255,0.12)]"
                  >
                    <span
                      className="pointer-events-none absolute inset-0 rounded-xl bg-gradient-to-br from-white/[0.1] via-white/[0.02] to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                      aria-hidden
                    />
                    <Icon className="relative z-10 w-[18px] h-[18px] transition-transform duration-300 group-hover:scale-110" aria-hidden />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {footerSections.map((section, index) => (
            <div key={index}>
              <h4 className="text-lg font-bold text-white mb-4">{section.title}</h4>
              <ul className="space-y-2">
                {section.links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <button
                      onClick={link.action}
                      className="text-white/40 hover:text-yellow-500 transition-colors text-left text-sm font-light"
                    >
                      {link.text}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/30 text-sm text-center md:text-left font-light">
              {t.footer.copyright}
            </p>
            <p className="text-white/30 text-sm flex items-center gap-2 font-light">
              {t.footer.madeWith}
              <Heart className="w-4 h-4 text-red-500 animate-pulse" />
              {t.footer.location}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
